import { useEffect, useState } from 'react';

import { Navigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { AdminOrganisationLayout } from './admin-organisation-layout';
import { SwitchPricingModelModal } from './switch-pricing-model-modal';
import { Permission } from '../../../../../../../backend/src/auth/permissions/permission';
import {
  ORG_PERMISSION_CONFIGS,
  ORG_PERMISSION_SECTIONS,
  OrgPermission,
  OrgPermissionConfig,
} from '../../../../../../../backend/src/organisation/organisation-permission';
import { PricingModel } from '../../../../../../../backend/src/types/types';
import { useSetOrganisationPermission } from '@/api/mutations/useSetOrganisationPermission';
import { useUpdateOrganisationBillingPeriod } from '@/api/mutations/useUpdateOrganisationBillingPeriod';
import { useUpdateOrganisationBlocking } from '@/api/mutations/useUpdateOrganisationBlocking';
import { useUpdateOrganisationIsTrialPackage } from '@/api/mutations/useUpdateOrganisationIsTrialPackage';
import { useUpdateSlackChannel } from '@/api/mutations/useUpdateSlackChannel';
import useGetOrganisation from '@/api/queries/useGetOrganisation';
import { dateToDatetimeLocal } from '@/helpers/formatDate';
import { useHasPermission } from '@/hooks/useHasPermission';
import { usePlatformPermissionCheck } from '@/hooks/usePlatformPermissionCheck';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { Icon } from '@/new/components/icon';
import {
  SettingsContent,
  SettingsRow,
  SettingsSection,
  SettingsSectionFooter,
} from '@/new/components/settings-section';
import { Button } from '@/new/components/shadcn/button';
import { Input } from '@/new/components/shadcn/input';
import { Switch } from '@/new/components/shadcn/switch';
import { TooltipIcon } from '@/new/components/tooltip-icon';
import { CreditLimits } from '@/new/pages/organisation-settings/components/credit-limits';

const isUsingOnboardingPackage = {
  id: 'onboarding-package-toggle',
  title: 'Onboarding Package',
  description:
    'Whether this organisation is currently on an onboarding package with increased usage limits. This will be indicated in the usage dashboard (and otherwise has no other effects).',
};

function PermissionRow({
  permission,
  isEnabled,
  onCheckedChange,
  disabled,
  isLoading = false,
}: {
  permission: Pick<OrgPermissionConfig, 'id' | 'title' | 'description'>;
  isEnabled: boolean;
  onCheckedChange: (enabled: boolean) => void;
  disabled?: boolean;
  isLoading?: boolean;
}) {
  const [pendingEnabled, setPendingEnabled] = useState<boolean | null>(null);

  return (
    <SettingsRow title={permission.title} description={permission.description}>
      <Switch
        checked={isEnabled}
        disabled={disabled || isLoading}
        id={permission.id}
        onCheckedChange={(enabled) => setPendingEnabled(enabled)}
      />

      <ConfirmationModal
        isOpen={pendingEnabled !== null}
        onOpenChange={(open) => {
          if (!open) setPendingEnabled(null);
        }}
        title={pendingEnabled ? 'Enable permission' : 'Disable permission'}
        onConfirm={() => {
          if (pendingEnabled === null) return;
          onCheckedChange(pendingEnabled);
          setPendingEnabled(null);
        }}
        isLoading={isLoading}
      >
        <p>
          Are you sure you want to {pendingEnabled ? 'enable' : 'disable'} the{' '}
          <span className="font-semibold">{permission.title}</span> permission for this organisation?
        </p>
      </ConfirmationModal>
    </SettingsRow>
  );
}

export function AdminManageOrganisation({
  section = 'configuration',
}: {
  section?: 'configuration' | 'billing' | 'general';
}) {
  const { organisationId } = useParams();
  if (!organisationId) return null;

  usePlatformPermissionCheck([Permission.PlatformManageOrganisations, Permission.PlatformManageBilling]);
  const { hasPermission } = useHasPermission();
  const canManageOrganisation = hasPermission(Permission.PlatformManageOrganisations);
  const canManageBilling = hasPermission(Permission.PlatformManageBilling);
  const canManageUsers = hasPermission(Permission.PlatformManageUsers);

  const { data: organisation } = useGetOrganisation(organisationId);

  const { mutate: updateBlocking } = useUpdateOrganisationBlocking(organisationId);
  const { mutate: setOrganisationPermission, isLoading: isSettingPermission } =
    useSetOrganisationPermission(organisationId);
  const { mutate: updateBillingPeriod, isLoading: isUpdatingBillingPeriod } =
    useUpdateOrganisationBillingPeriod(organisationId);
  const { mutate: updateIsTrialPackage } = useUpdateOrganisationIsTrialPackage(organisationId);
  const { mutate: updateSlackChannel, isLoading: isUpdatingSlackChannel } = useUpdateSlackChannel(organisationId);

  const [maxCredits, setMaxCredits] = useState<number | undefined>();
  const [seatCount, setSeatCount] = useState<number | undefined>();
  const [billingStartDate, setBillingStartDate] = useState<Date | undefined>();
  const [billingEndDate, setBillingEndDate] = useState<Date | undefined>();
  const [billingDetailsChanged, setBillingDetailsChanged] = useState(false);
  const [slackChannelId, setSlackChannelId] = useState<string>('');
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
  const [defaultCreditLimit, setDefaultCreditLimit] = useState<number | undefined>();

  const isSeatBased = organisation?.pricingModel === PricingModel.SeatBased;

  function setPermission(permission: OrgPermission, enabled: boolean) {
    if (!organisation) return;

    setOrganisationPermission({ permission, enabled });
  }

  useEffect(() => {
    setMaxCredits(organisation?.maxCredits ?? undefined);
    setSeatCount(organisation?.seatCount ?? undefined);
    setBillingStartDate(organisation?.billingStartDate ? new Date(organisation.billingStartDate) : undefined);
    setBillingEndDate(organisation?.billingEndDate ? new Date(organisation?.billingEndDate) : undefined);
    setSlackChannelId(organisation?.slackChannelId ?? '');
    setDefaultCreditLimit(organisation?.defaultCreditLimit ?? undefined);
  }, [organisation]);

  function handleBillingPeriodSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!billingStartDate || !billingEndDate) {
      toast.error(`Billing period dates can't be empty`);
      return;
    }

    if (isSeatBased) {
      if (seatCount === undefined) {
        toast.error(`Seat count can't be empty`);
        return;
      }
      if (defaultCreditLimit === undefined || defaultCreditLimit < 1) {
        toast.error(`Default credit limit can't be empty`);
        return;
      }
      updateBillingPeriod({
        seatCount,
        defaultCreditLimit,
        billingStartDate,
        billingEndDate,
      });
    } else {
      if (maxCredits === undefined) {
        toast.error(`Maximum credits can't be empty`);
        return;
      }
      updateBillingPeriod({
        maxCredits,
        billingStartDate,
        billingEndDate,
      });
    }
  }

  useEffect(() => {
    if (!organisation) return setBillingDetailsChanged(false);

    if (organisation.billingStartDate === null || organisation.billingEndDate === null) {
      return setBillingDetailsChanged(true);
    }

    const datesChanged =
      billingStartDate?.getTime() !== new Date(organisation.billingStartDate).getTime() ||
      billingEndDate?.getTime() !== new Date(organisation.billingEndDate).getTime();

    const configChanged = isSeatBased
      ? seatCount !== (organisation.seatCount ?? undefined) ||
        defaultCreditLimit !== (organisation.defaultCreditLimit ?? undefined)
      : maxCredits !== (organisation.maxCredits ?? undefined) || organisation.maxCredits === null;

    setBillingDetailsChanged(datesChanged || configChanged);
  }, [organisation, maxCredits, seatCount, defaultCreditLimit, billingStartDate, billingEndDate, isSeatBased]);

  if (section === 'configuration' && !canManageOrganisation && canManageBilling) {
    return <Navigate to={`/admin/organisations/${organisationId}/billing`} replace />;
  }

  return (
    <AdminOrganisationLayout>
      {!organisation ? (
        // TODO: improve skeleton/move into generic component
        <div className="pulse my-8 h-8 w-sm rounded-lg bg-gray-50"></div>
      ) : (
        <>
          {section === 'configuration' && canManageOrganisation && (
            <SettingsContent>
              {ORG_PERMISSION_SECTIONS.map((permissionSection) => {
                const configs = ORG_PERMISSION_CONFIGS.filter((config) => config.kind === permissionSection.kind);
                return (
                  <SettingsSection
                    key={permissionSection.kind}
                    title={permissionSection.title}
                    description={permissionSection.description}
                  >
                    {configs.length === 0 ? (
                      <SettingsRow title={permissionSection.emptyText} />
                    ) : (
                      configs.map((permission) => (
                        <PermissionRow
                          key={permission.id}
                          permission={permission}
                          isEnabled={organisation.permissions.includes(permission.permission)}
                          onCheckedChange={(enabled) => setPermission(permission.permission, enabled)}
                          isLoading={isSettingPermission}
                        />
                      ))
                    )}
                  </SettingsSection>
                );
              })}
            </SettingsContent>
          )}

          {section === 'billing' && canManageBilling && (
            <SettingsContent>
              <SwitchPricingModelModal
                isOpen={isPricingModalOpen}
                onOpenChange={setIsPricingModalOpen}
                organisation={organisation}
              />

              <SettingsSection title="Billing" description="Configure the organisation's billing model and limits.">
                <SettingsRow
                  title="Pricing model"
                  description={
                    <>
                      Currently set to{' '}
                      <span className="font-medium text-gray-700">
                        {organisation.pricingModel === PricingModel.SeatBased ? 'seat-based' : 'usage-based'}
                      </span>
                      .
                      {isSeatBased && canManageUsers && (
                        <>
                          {' '}
                          Per-user limits can be managed from the{' '}
                          <a
                            href={`/admin/organisations/${organisation.id}/users`}
                            className="font-medium text-gray-700 underline hover:text-gray-900"
                          >
                            Users
                          </a>{' '}
                          tab.
                        </>
                      )}
                    </>
                  }
                >
                  <Button onClick={() => setIsPricingModalOpen(true)}>
                    <Icon className="lucide--arrow-left-right" />
                    Switch
                  </Button>
                </SettingsRow>
                <PermissionRow
                  permission={isUsingOnboardingPackage}
                  isEnabled={organisation.isUsingTrialPackage}
                  onCheckedChange={() =>
                    updateIsTrialPackage({ isUsingTrialPackage: !organisation.isUsingTrialPackage })
                  }
                />
                <PermissionRow
                  permission={{
                    id: 'blocking-toggle',
                    title: 'Blocking Enabled',
                    description:
                      'When enabled, users/organisations who exceed their usage limits will be blocked from further uploads and runs.',
                  }}
                  isEnabled={organisation.isBlockingEnabled}
                  onCheckedChange={() =>
                    updateBlocking(
                      {
                        isBlockingEnabled: !organisation.isBlockingEnabled,
                      },
                      {
                        onSuccess: () => {
                          toast.success(
                            `Blocking ${organisation.isBlockingEnabled ? 'disabled' : 'enabled'} for this organisation`,
                          );
                        },
                      },
                    )
                  }
                />
              </SettingsSection>

              <CreditLimits
                organisation={organisation}
                organisationId={organisationId}
                canManage
                includeContentWrapper={false}
                showReviewerInstructions={false}
              />

              <form onSubmit={handleBillingPeriodSubmit}>
                <SettingsSection
                  title="Billing period"
                  description="Billing periods are append-only: writing a new period retains the existing records rather than editing them. Historical billing periods will be viewable here in future."
                  footer={
                    <SettingsSectionFooter className="justify-end">
                      <Button
                        variant="primary"
                        type="submit"
                        disabled={isUpdatingBillingPeriod || !billingDetailsChanged}
                      >
                        Write new billing period
                      </Button>
                    </SettingsSectionFooter>
                  }
                >
                  {isSeatBased ? (
                    <>
                      <SettingsRow title="Number of seats">
                        <Input
                          className="w-48"
                          type="number"
                          id="seat-count"
                          value={seatCount ?? ''}
                          onChange={(e) => setSeatCount(Number(e.target.value))}
                          disabled={isUpdatingBillingPeriod}
                          min="1"
                        />
                      </SettingsRow>
                      <SettingsRow
                        title={
                          <span className="flex items-center gap-1.5">
                            Default credit limit
                            <TooltipIcon
                              tooltipContent="Applied to every current user in the org (resetting per-user overrides) and used as the starting limit for any new user added later."
                              triggerClassName="h-3.5 w-3.5"
                            />
                          </span>
                        }
                      >
                        <Input
                          className="w-48"
                          type="number"
                          id="default-credit-limit"
                          value={defaultCreditLimit ?? ''}
                          onChange={(e) => setDefaultCreditLimit(Number(e.target.value))}
                          disabled={isUpdatingBillingPeriod}
                          min="1"
                        />
                      </SettingsRow>
                    </>
                  ) : (
                    <SettingsRow title="Maximum credits">
                      <Input
                        className="w-48"
                        type="number"
                        id="max-credits"
                        value={maxCredits ?? ''}
                        onChange={(e) => setMaxCredits(Number(e.target.value))}
                        disabled={isUpdatingBillingPeriod}
                      />
                    </SettingsRow>
                  )}

                  <SettingsRow title="Billing period start date">
                    <Input
                      className="w-60"
                      type="datetime-local"
                      id="start-date"
                      value={billingStartDate ? dateToDatetimeLocal(billingStartDate) : ''}
                      onChange={(e) => setBillingStartDate(new Date(e.target.value))}
                      disabled={isUpdatingBillingPeriod}
                    />
                  </SettingsRow>

                  <SettingsRow title="Billing period end date">
                    <Input
                      className="w-60"
                      type="datetime-local"
                      id="end-date"
                      value={billingEndDate ? dateToDatetimeLocal(billingEndDate) : ''}
                      onChange={(e) => setBillingEndDate(new Date(e.target.value))}
                      disabled={isUpdatingBillingPeriod}
                    />
                  </SettingsRow>
                </SettingsSection>
              </form>
            </SettingsContent>
          )}

          {section === 'general' && canManageOrganisation && (
            <SettingsContent>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  updateSlackChannel(
                    { slackChannelId: slackChannelId.trim() },
                    {
                      onSuccess: () => {
                        toast.success('Slack channel updated');
                      },
                    },
                  );
                }}
              >
                <SettingsSection
                  title="General"
                  footer={
                    <SettingsSectionFooter className="justify-end">
                      <Button
                        variant="primary"
                        type="submit"
                        disabled={isUpdatingSlackChannel || slackChannelId === (organisation?.slackChannelId ?? '')}
                      >
                        Update Slack Channel
                      </Button>
                    </SettingsSectionFooter>
                  }
                >
                  <SettingsRow
                    title="Wexler Slack Channel ID"
                    description="Messages about runs and organisation events will be routed here. Leave empty to use the default monitoring channel."
                  >
                    <Input
                      className="w-64"
                      type="text"
                      id="slack-channel-id"
                      placeholder="e.g. C0123456789"
                      value={slackChannelId}
                      onChange={(e) => setSlackChannelId(e.target.value)}
                      disabled={isUpdatingSlackChannel}
                    />
                  </SettingsRow>
                </SettingsSection>
              </form>
            </SettingsContent>
          )}
        </>
      )}
    </AdminOrganisationLayout>
  );
}
